/**
 * Assess Deduction (prototype v108 §3.8; handoff v109 §6; decided 24 Sep 2026: Admin and Billing assess and reverse).
 * Pure: the booking's talent, rates and deduction rows in; pay figures and validated new rows out.
 *
 * Original pay = hours booked × the talent's hourly rate ($40 when none is set, as the current Pay Talent dialog
 * uses). A deduction never changes the original; it is its own row, can stack, and is reversed (not deleted).
 * Pay due = original − active deductions, never below zero.
 */

import { normalizePackageType } from '../constants/packagePricing.js'
import { videoAddonHours } from '../addons/videoAddonHours.js'

export const DEFAULT_HOURLY_RATE = 40

/** The reason schedule with its guidance, exactly as the prototype shows it. The amount is always typed by hand. */
export const DEDUCTION_REASONS = [
  { key: 'late_reachout', label: 'Late Reachout', guidance: '$25 pay deduction if Talent does not manually message the client via portal at least 3 weeks before the wedding.' },
  { key: 'late_upload', label: 'Late Upload', guidance: '1 day = $25 · 2 days = $50 · 3 days = $75 · 4 days = $100 · 5+ days = $200.' },
  { key: 'upload_without_jcf', label: 'Upload Without Job Completion Form', guidance: '1 day = $20 · 2 days = $40 · 3 days = $60 · any further = flat 50% payment withheld.' },
  { key: 'under_delivery', label: 'Under-delivery', guidance: 'Photos under 100/hr: $25–$100 depending on severity.' },
  { key: 'missing_raw_jpeg', label: 'Missing RAW or JPEG Files', guidance: '$75–full payment withheld.' },
  { key: 'video_format', label: 'Incorrect Video Format', guidance: '$50–$150 depending on the issue.' },
  { key: 'video_resolution', label: 'Video Under 1080p', guidance: '$50–$150 depending on the issue.' },
  { key: 'corrupted_files', label: 'Corrupted or Incomplete Files/Uploads', guidance: '$75–$150 case-by-case.' },
  { key: 'unprofessional', label: 'Extreme Unprofessional Conduct', guidance: '$200 + blacklist.' },
  { key: 'tardiness', label: 'Event Tardiness', guidance: 'Up to 15 min = $25 · 15–30 min = $50 · >30 min = $150.' },
  { key: 'no_show', label: 'No-show / Lack of Notice', guidance: '100% payment withheld + blacklist.' },
  { key: 'moments_missed', label: 'Core & Requested Moments Uncaptured', guidance: '$100 minimum depending on severity + number of moments missed.' },
  { key: 'qbo_incomplete', label: 'QBO Onboarding Incomplete', guidance: '90 days post-event: 50–100% pay withheld.' },
  { key: 'other', label: 'Other', guidance: 'Enter the deduction amount and a required description explaining the reason.' }
]

export const ROLE_LABEL = { photographer: 'Photographer', second_photographer: 'Second Photographer', videographer: 'Videographer', second_videographer: 'Second Videographer' }
const PLACEHOLDERS = new Set(['410935f9-6034-40b3-a65f-e9863de60e58', '7c7191dc-c5e5-43ed-b772-083463a00aa4'])

export const money = cents => `$${(Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/** hours × rate, in cents. */
export function originalPayCents(hours, hourlyRate) {
  const h = Number(hours)
  const r = Number(hourlyRate) > 0 ? Number(hourlyRate) : DEFAULT_HOURLY_RATE
  return Number.isFinite(h) && h > 0 ? Math.round(h * r * 100) : 0
}

export const activeDeductions = rows => (rows || []).filter(d => !d.reversed_at)
export const deductedCents = rows => activeDeductions(rows).reduce((s, d) => s + Number(d.amount_cents || 0), 0)
export const payDueCents = (original, rows) => Math.max(0, Number(original || 0) - deductedCents(rows))

const VIDEO_ROLES = new Set(['videographer', 'second_videographer'])

/**
 * Hours one role is paid for (Nautica via Traci, 19 Oct 2026: "a videographer on a video add-on shows the wrong total").
 * In order: hours set on this booking for that talent, then the Video Add-On's hours for a videographer on a booking
 * whose package has no video, then the booking's hours. Coverage hours are otherwise never split.
 */
export function roleHours(booking, role, set = null) {
  const own = Number(set?.hours)
  if (Number.isFinite(own) && own > 0) return { hours: own, hours_source: 'booking' }
  if (VIDEO_ROLES.has(role)) {
    const type = normalizePackageType(booking?.package_type)
    if (type !== 'photoVideo' && type !== 'videoOnly') {
      const h = videoAddonHours(booking)
      if (h) return { hours: h, hours_source: 'video_addon' }
    }
  }
  return { hours: Number(booking?.hours_booked) || 0, hours_source: 'booking_hours' }
}

/**
 * The talent on a booking with their pay figures.
 * @param booking     { hours_booked, package_type, photographer_id, second_photographer_id, videographer_id, and the
 *                    add-on fields videoAddonHours reads }
 * @param talent      Map photographers.id -> { name, hourly_rate }
 * @param deductions  talent_deductions rows for the booking
 * @param rates       Map role -> booking_talent_rates row ({ hourly_rate, hours, set_by_name, set_at, note }) (Traci's
 *                    doc, Oct 2026: a rate set on the booking overrides the talent's base rate; deductions come off that.
 *                    19 Oct 2026: hours too, either of them may be empty. 21 Oct 2026: flat_cents / flat_reason, a flat
 *                    amount on top, e.g. $50 travel)
 */
export function talentPay(booking, talent = new Map(), deductions = [], rates = new Map()) {
  const out = []
  for (const role of ['photographer', 'second_photographer', 'videographer', 'second_videographer']) {
    const id = booking?.[`${role}_id`]
    if (!id || PLACEHOLDERS.has(id)) continue
    const t = talent.get(id) || {}
    const rows = (deductions || []).filter(d => d.role === role && d.talent_id === id).sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
    const base = Number(t.hourly_rate) > 0 ? Number(t.hourly_rate) : DEFAULT_HOURLY_RATE
    const set = rates?.get?.(role) || null
    const ownRate = set && Number(set.hourly_rate) > 0
    const rate = ownRate ? Number(set.hourly_rate) : base
    const { hours, hours_source } = roleHours(booking, role, set)
    const fallback = roleHours(booking, role, null)
    const hourly = originalPayCents(hours, rate)
    const flat = set && Number(set.flat_cents) > 0 ? Math.round(Number(set.flat_cents)) : 0
    const original = hourly + flat
    out.push({
      role, role_label: ROLE_LABEL[role], talent_id: id, name: t.name || 'Talent', hourly_rate: rate, base_hourly_rate: base,
      rate_source: ownRate ? 'booking' : Number(t.hourly_rate) > 0 ? 'talent' : 'default', booking_rate: ownRate ? { hourly_rate: rate, set_by_name: set.set_by_name || null, set_at: set.set_at || null, note: set.note || null } : null,
      hours, hours_source, default_hours: fallback.hours, default_hours_source: fallback.hours_source, booking_set: set ? { hourly_rate: ownRate ? rate : null, hours: hours_source === 'booking' ? hours : null } : null,
      hourly_cents: hourly, flat_cents: flat, flat_reason: flat ? set.flat_reason || null : null,
      original_cents: original, deducted_cents: deductedCents(rows), due_cents: payDueCents(original, rows), deductions: rows
    })
  }
  return out
}

/**
 * Validate a new deduction (the prototype's rules).
 * @param input  { role, reason_key, amount (dollars), details }
 * @param pay    the talentPay() entry for that role
 */
export function planDeduction(input = {}, pay = null) {
  if (!pay) return { ok: false, errors: ['Pick the talent'] }
  const errors = []
  const reason = DEDUCTION_REASONS.find(r => r.key === input.reason_key)
  if (!reason) errors.push('Pick a deduction reason')
  const cents = Math.round(Number(input.amount) * 100)
  if (!Number.isFinite(cents) || cents <= 0) errors.push('Enter an amount greater than $0')
  const details = String(input.details || '').trim()
  if (reason?.key === 'other' && !details) errors.push('Description is required when Other is selected.')
  if (Number.isFinite(cents) && cents > pay.due_cents) errors.push('Deduction cannot exceed the current adjusted Talent payable.')
  if (errors.length) return { ok: false, errors }
  return {
    ok: true,
    errors: [],
    row: { talent_id: pay.talent_id, role: pay.role, reason_key: reason.key, reason_label: reason.label, details: details || null, amount_cents: cents, original_cents: pay.original_cents },
    after_cents: pay.due_cents - cents,
    note: `Talent Pay Deduction: ${pay.name} · −${money(cents)} · ${reason.label}${details ? ` · ${details}` : ''}`
  }
}

/** The locked booking note written when a deduction is reversed. */
export function reversalNote(name, row, reason) {
  return `Talent Pay Deduction Reversed: ${name} · +${money(row.amount_cents)} restored · ${row.reason_label}${reason ? ` · ${reason}` : ''}`
}

/**
 * A deduction flag for Billing (Traci's Loom, 28 Sep 2026): Editing, Talent or Billing pick the categories that apply
 * to one talent and say why; no amount. Billing settles it on Pay Talent (resolveFlagPlan).
 * @param input  { role, reason_keys: string[], note }
 * @param pay    the talentPay() entry for that role
 */
export function planDeductionFlag(input = {}, pay = null) {
  if (!pay) return { ok: false, errors: ['Pick the talent'] }
  const keys = [...new Set((Array.isArray(input.reason_keys) ? input.reason_keys : []).map(String))]
  const reasons = keys.map(k => DEDUCTION_REASONS.find(r => r.key === k)).filter(Boolean)
  const note = String(input.note || '').trim().slice(0, 2000)
  const errors = []
  if (!reasons.length || reasons.length !== keys.length) errors.push('Pick at least one deduction category')
  if (note.length < 5) errors.push('Say what happened, for Billing')
  if (errors.length) return { ok: false, errors }
  const labels = reasons.map(r => r.label).join(', ')
  return {
    ok: true,
    errors: [],
    row: { role: pay.role, talent_id: pay.talent_id, reason_keys: reasons.map(r => r.key), note },
    labels,
    note: `Deduction flagged for Billing: ${pay.name} (${pay.role_label}) · ${labels} · ${note}`
  }
}

/** Labels for a flag's categories, in schedule order. */
export const flagLabels = keys => DEDUCTION_REASONS.filter(r => (keys || []).includes(r.key)).map(r => r.label)

/**
 * What Billing sees before paying (Kendra, 30 Oct 2026: "one place" for Elizabeth's deductions, Nautica's rate changes
 * and travel pay): this talent's pay changes on the booking, newest first, as short lines.
 * @param pay    the talentPay() entry
 * @param flags  talent_deduction_flags rows for the booking
 */
export function payNotes(pay, flags = []) {
  if (!pay) return []
  const out = []
  if (pay.booking_rate) out.push({ kind: 'rate', text: `Rate for this booking: $${pay.booking_rate.hourly_rate}/h instead of $${pay.base_hourly_rate}/h`, by: pay.booking_rate.set_by_name || null, at: pay.booking_rate.set_at || null, note: pay.booking_rate.note || null })
  if (pay.hours_source === 'booking') out.push({ kind: 'hours', text: `Hours for this booking: ${pay.hours} h instead of ${pay.default_hours} h`, by: null, at: null, note: null })
  if (pay.hours_source === 'video_addon') out.push({ kind: 'hours', text: `Paid for the Video Add-On's ${pay.hours} h`, by: null, at: null, note: null })
  if (pay.flat_cents) out.push({ kind: 'flat', text: `Plus ${money(pay.flat_cents)}${pay.flat_reason ? ` ${pay.flat_reason}` : ''}`, by: null, at: null, note: null })
  for (const d of activeDeductions(pay.deductions)) out.push({ kind: 'deduction', text: `Deduction −${money(d.amount_cents)}: ${d.reason_label}`, by: d.assessed_by_name || null, at: d.created_at || null, note: d.details || null })
  for (const f of (flags || []).filter(x => x.role === pay.role && x.talent_id === pay.talent_id && x.status === 'open')) {
    out.push({ kind: 'flag', flag_id: f.id, reason_keys: f.reason_keys, text: `Flagged: ${flagLabels(f.reason_keys).join(', ')}`, by: f.flagged_by_name || null, at: f.created_at || null, note: f.note || null })
  }
  return out
}

export const MAX_HOURLY_RATE = 1000

/**
 * Set or clear the hourly rate for one role on this booking (Admin, Talent, Billing). `hourly_rate` empty / null clears
 * it, so the talent's base rate applies again.
 * @param input { role, hourly_rate, note }
 * @param pay   the talentPay() entry for that role (the talent must be on the booking)
 */
export function planBookingRate(input = {}, pay = null) {
  if (!pay) return { ok: false, errors: ['Pick the talent on this booking'] }
  const raw = input.hourly_rate
  const note = String(input.note || '').trim().slice(0, 500) || null
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    if (pay.rate_source !== 'booking') return { ok: false, errors: ['This booking already uses the base rate'] }
    return { ok: true, action: 'clear', errors: [], note: `Talent rate for this booking removed: ${pay.name} (${pay.role_label}) back to the base rate $${pay.base_hourly_rate}/h${note ? ` · ${note}` : ''}` }
  }
  const rate = Math.round(Number(raw) * 100) / 100
  if (!Number.isFinite(rate) || rate <= 0) return { ok: false, errors: ['Enter an hourly rate greater than $0'] }
  if (rate > MAX_HOURLY_RATE) return { ok: false, errors: [`Enter an hourly rate of $${MAX_HOURLY_RATE} or less`] }
  if (pay.rate_source === 'booking' && rate === pay.hourly_rate) return { ok: false, errors: ['That is already the rate on this booking'] }
  const after = originalPayCents(pay.hours, rate)
  return {
    ok: true, action: 'set', errors: [], hourly_rate: rate, original_cents: after, due_cents: Math.max(0, after - pay.deducted_cents),
    note: `Talent rate for this booking: ${pay.name} (${pay.role_label}) $${rate}/h instead of $${pay.base_hourly_rate}/h · pay ${money(after)} before deductions${note ? ` · ${note}` : ''}`
  }
}

export const MAX_HOURS = 24

/**
 * Set or clear the hours one talent is paid for on this booking (Nautica via Traci, 19 Oct 2026: "adjust coverage hours
 * as well for each talent, not just the rate"). Empty / null clears it, so the booking's hours (or the Video Add-On's,
 * for a videographer on an add-on) apply again.
 * @param input { role, hours, note }
 * @param pay   the talentPay() entry for that role
 */
export function planBookingHours(input = {}, pay = null) {
  if (!pay) return { ok: false, errors: ['Pick the talent on this booking'] }
  const raw = input.hours
  const note = String(input.note || '').trim().slice(0, 500) || null
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    if (pay.hours_source !== 'booking') return { ok: false, errors: ['This talent already uses the booking hours'] }
    return { ok: true, action: 'clear', errors: [], note: `Talent hours for this booking removed: ${pay.name} (${pay.role_label}) back to ${pay.default_hours} h${pay.default_hours_source === 'video_addon' ? ' (the Video Add-On hours)' : ''}${note ? ` · ${note}` : ''}` }
  }
  const hours = Math.round(Number(raw) * 4) / 4
  if (!Number.isFinite(hours) || hours <= 0) return { ok: false, errors: ['Enter hours greater than 0'] }
  if (hours > MAX_HOURS) return { ok: false, errors: [`Enter ${MAX_HOURS} hours or less`] }
  if (pay.hours_source === 'booking' && hours === pay.hours) return { ok: false, errors: ['Those are already the hours on this booking'] }
  const after = originalPayCents(hours, pay.hourly_rate)
  return {
    ok: true, action: 'set', errors: [], hours, original_cents: after, due_cents: Math.max(0, after - pay.deducted_cents),
    note: `Talent hours for this booking: ${pay.name} (${pay.role_label}) ${hours} h instead of ${pay.hours} h · pay ${money(after)} before deductions${note ? ` · ${note}` : ''}`
  }
}

export const MAX_FLAT_CENTS = 500000

/** "2 h × $40/h = $80.00 + $50.00 travel = $130.00", or just "2 h × $40/h = $80.00" with no flat amount. */
export function payLine(pay) {
  const hourly = `${pay.hours} h × $${pay.hourly_rate}/h = ${money(pay.hourly_cents ?? pay.original_cents)}`
  return pay.flat_cents ? `${hourly} + ${money(pay.flat_cents)}${pay.flat_reason ? ` ${pay.flat_reason}` : ''} = ${money(pay.original_cents)}` : hourly
}

/**
 * Set or clear a flat amount on top of hours × rate for one talent on this booking (Nautica via Traci, 21 Oct 2026:
 * "sometimes she gives them an extra fifty dollars for travel ... two hours, forty dollars an hour ... eighty dollars
 * ... plus fifty ... one thirty"). `flat_amount` in dollars; empty / null clears it. `flat_reason` says what it is for.
 */
export function planBookingFlat(input = {}, pay = null) {
  if (!pay) return { ok: false, errors: ['Pick the talent on this booking'] }
  const raw = input.flat_amount
  const reason = String(input.flat_reason || '').trim().slice(0, 80) || null
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    if (!pay.flat_cents) return { ok: false, errors: ['There is no flat amount on this booking'] }
    const after = pay.original_cents - pay.flat_cents
    return { ok: true, action: 'clear', errors: [], note: `Talent pay for this booking: ${pay.name} (${pay.role_label}) flat amount removed (${money(pay.flat_cents)}${pay.flat_reason ? ` ${pay.flat_reason}` : ''}) · pay ${money(after)} before deductions` }
  }
  const cents = Math.round(Number(raw) * 100)
  if (!Number.isFinite(cents) || cents <= 0) return { ok: false, errors: ['Enter a flat amount greater than $0'] }
  if (cents > MAX_FLAT_CENTS) return { ok: false, errors: [`Enter ${money(MAX_FLAT_CENTS)} or less`] }
  if (!reason) return { ok: false, errors: ['Say what the flat amount is for (for example, travel)'] }
  const after = (pay.hourly_cents ?? pay.original_cents - (pay.flat_cents || 0)) + cents
  const next = { ...pay, flat_cents: cents, flat_reason: reason, original_cents: after }
  return {
    ok: true, action: 'set', errors: [], flat_cents: cents, flat_reason: reason, original_cents: after, due_cents: Math.max(0, after - pay.deducted_cents),
    note: `Talent pay for this booking: ${pay.name} (${pay.role_label}) ${payLine(next)} before deductions`
  }
}
